import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Button } from '../components/ui/button';
import { Check, ArrowRight, Search, FileText, Globe, Link as LinkIcon, Users, Shield, DollarSign, MapPin, Zap, ChevronDown, ChevronUp, MessageSquare, Sparkles } from 'lucide-react';
import AuthModal from '../components/AuthModal';

const IndustryMartialArts = ({ user, onLogin }) => {
  const navigate = useNavigate();
  const [showAuth, setShowAuth] = useState(false);
  const [expandedFaq, setExpandedFaq] = useState(null);

  const handleGetStarted = () => {
    if (user) {
      navigate('/dashboard');
    } else {
      setShowAuth(true);
    }
  };

  const handleLogin = (userData, token) => {
    localStorage.setItem('token', token);
    localStorage.setItem('user', JSON.stringify(userData));
    setShowAuth(false);
    window.location.href = '/dashboard';
  };

  // Question categories with real questions
  const questionCategories = [
    {
      icon: <Users className="w-6 h-6" />,
      title: "Age & Readiness",
      color: "blue",
      questions: [
        "What age should my child start martial arts?",
        "Is martial arts good for ADHD?",
        "Is martial arts good for shy kids?",
        "What's best for a 4-year-old: karate or taekwondo?",
        "How long does it take a child to get a belt?"
      ]
    },
    {
      icon: <Shield className="w-6 h-6" />,
      title: "Safety & Confidence",
      color: "green",
      questions: [
        "Is Brazilian jiu-jitsu safe for kids?",
        "Can a child get hurt in martial arts?",
        "What's the safest martial art for kids?",
        "Does sparring happen in kids classes?",
        "How does martial arts help with bullying?"
      ]
    },
    {
      icon: <DollarSign className="w-6 h-6" />,
      title: "Pricing & Value",
      color: "orange",
      questions: [
        "How much do martial arts classes cost per month?",
        "Are martial arts contracts worth it?",
        "What should I look for in a kids martial arts program?",
        "What's included in a martial arts membership?",
        "Are uniforms and belts extra?"
      ]
    },
    {
      icon: <MapPin className="w-6 h-6" />,
      title: 'Local / "Near Me" Intent',
      color: "purple",
      questions: [
        "Best kids martial arts classes near me",
        "Best martial arts school for kids in [CITY]",
        "Kids karate classes in [CITY]",
        "Martial arts for toddlers near me",
        "Family martial arts classes near me"
      ]
    }
  ];

  // 7-day publishing plan
  const publishingPlan = [
    "What age should a child start martial arts?",
    "Is martial arts good for ADHD?",
    "How much do martial arts classes cost per month?",
    "Is jiu-jitsu safe for kids?",
    "Karate vs Taekwondo for kids: which is better?",
    "Best kids martial arts classes in [CITY]",
    "What should I look for in a martial arts school?"
  ];

  // FAQ items
  const faqs = [
    {
      q: "Does this help with 'near me' searches?",
      a: "Yes - QuillFire includes local-intent questions and structures posts so your city and services naturally appear."
    },
    {
      q: "Will posts mention my programs?",
      a: "Yes - your profile includes programs and audiences, and QuillFire uses those details in each post."
    },
    {
      q: "Can I edit posts before publishing?",
      a: "Yes - everything saves to your Content Library for editing before publishing."
    },
    {
      q: "Are the questions real?",
      a: "They're based on real demand data and conversational search patterns, with search volume included."
    },
    {
      q: "How often do I need to run discovery?",
      a: "Usually once per month (or less). Generate multiple posts from one discovery run."
    },
    {
      q: "Do I need a blog already?",
      a: "No. You can export posts and publish anywhere."
    }
  ];

  // Sample blog preview structure
  const sampleBlogOutline = [
    "The best starting ages (toddlers vs school-age vs teens)",
    "What 'ready' looks like (focus, listening, separation)",
    "Which program fits best (Little Dragons vs Kids Martial Arts)",
    "Safety + confidence benefits parents care about",
    "FAQ: common parent questions",
    "CTA: book an intro / claim the special (customized)"
  ];

  const sampleFaqs = [
    "Is martial arts okay for a shy child?",
    "How many classes per week is best for beginners?",
    "Does my child need to be athletic to start?"
  ];

  return (
    <div className="min-h-screen bg-white">
      <Helmet>
        <title>Martial Arts SEO Content Generator | Get Found in Google + AI Search</title>
        <meta name="description" content="Discover the real questions parents and adults ask before joining a martial arts school. Generate publish-ready answer posts built to rank in Google and get recommended in AI search." />
      </Helmet>

      {/* Navigation */}
      <nav className="bg-white/80 backdrop-blur-xl border-b border-gray-100 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center space-x-2 cursor-pointer" onClick={() => navigate('/')}>
            <img src="/quillfire-logo.png" alt="QuillFire" className="w-10 h-10 rounded-lg" />
            <span className="text-2xl font-bold text-gray-900">QuillFire</span>
          </div>
          <div className="flex items-center space-x-4">
            <a href="#questions" className="text-gray-600 hover:text-gray-900 hidden sm:block">Questions</a>
            <a href="#how-it-works" className="text-gray-600 hover:text-gray-900 hidden sm:block">How It Works</a>
            <a href="#pricing" className="text-gray-600 hover:text-gray-900 hidden sm:block">Pricing</a>
            {user ? (
              <Button onClick={() => navigate('/dashboard')} className="bg-orange-600 hover:bg-orange-700 text-white">
                Dashboard
              </Button>
            ) : (
              <Button onClick={handleGetStarted} className="bg-orange-600 hover:bg-orange-700 text-white">
                Start Free
              </Button>
            )}
          </div>
        </div>
      </nav>

      {/* HERO SECTION */}
      <section className="pt-16 pb-20 px-6 bg-gradient-to-b from-orange-50 to-white overflow-hidden">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            {/* Left: Text Content */}
            <div className="text-center lg:text-left">
              <h1 className="mb-8 text-gray-900 leading-tight">
                <span className="block text-4xl sm:text-5xl lg:text-6xl font-black">Be the</span>
                <span className="block text-4xl sm:text-5xl lg:text-6xl font-black">Martial Arts School</span>
                <span className="block text-4xl sm:text-5xl lg:text-6xl font-black text-orange-600">AI Recommends.</span>
              </h1>
              
              <p className="text-lg text-gray-600 mb-4 max-w-xl mx-auto lg:mx-0">
                Parents don't search in keywords anymore—they ask real questions into Google and AI.
              </p>
              
              <p className="text-lg text-gray-800 font-medium mb-6 max-w-xl mx-auto lg:mx-0 italic">
                "What age should a child start martial arts?" • "Is jiu-jitsu safe for kids?"
              </p>
              
              <p className="text-base text-gray-600 mb-6 max-w-xl mx-auto lg:mx-0">
                QuillFire finds those questions and generates publish-ready answer posts built to get found in Google and AI search.
              </p>

              {/* Trust line */}
              <p className="text-sm text-gray-500 mb-8">
                Real question discovery + search volume • 1,600-2,300 word posts • SEO + FAQs + CTAs included
              </p>

              {/* CTA Block */}
              <div className="flex flex-col items-center lg:items-start gap-4">
                <Button 
                  onClick={handleGetStarted}
                  className="bg-orange-600 hover:bg-orange-700 text-white text-lg px-8 py-6 rounded-xl font-semibold"
                >
                  Start Free Trial (5 Posts) →
                </Button>
                <div className="flex flex-wrap justify-center lg:justify-start gap-4 text-sm text-gray-500">
                  <span className="flex items-center gap-1"><Check className="w-4 h-4 text-green-500" /> No credit card required</span>
                  <span className="flex items-center gap-1"><Check className="w-4 h-4 text-green-500" /> 5 posts + 5 images included</span>
                </div>
              </div>
            </div>

            {/* Right: Hero Image */}
            <div className="relative hidden lg:block">
              <div className="relative rounded-2xl overflow-hidden shadow-2xl">
                <img 
                  src="https://images.unsplash.com/photo-1607031767898-5f319512ff1e?w=800&q=80" 
                  alt="Kids in martial arts class" 
                  className="w-full h-[500px] object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent"></div>
              </div>
              {/* Floating badge */}
              <div className="absolute -bottom-4 -left-4 bg-white rounded-xl shadow-lg p-4 border border-gray-100">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-orange-100 rounded-full flex items-center justify-center">
                    <Sparkles className="w-6 h-6 text-orange-600" />
                  </div>
                  <div>
                    <p className="font-bold text-gray-900">150+ Questions</p>
                    <p className="text-sm text-gray-500">Real search data</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Mobile: See questions link */}
          <div className="text-center mt-8 lg:hidden">
            <a href="#questions" className="text-gray-400 hover:text-orange-600 text-sm">
              See example questions ↓
            </a>
          </div>
        </div>
      </section>

      {/* QUICK BENEFITS BAR */}
      <section className="py-16 px-6 bg-gray-50 border-y border-gray-200">
        <div className="max-w-6xl mx-auto grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
          <div className="bg-white rounded-2xl p-8 border border-gray-200 shadow-sm hover:shadow-md hover:border-orange-200 transition-all duration-300 group">
            <div className="w-14 h-14 bg-orange-100 rounded-xl flex items-center justify-center mb-4 group-hover:bg-orange-200 transition-colors">
              <Search className="w-7 h-7 text-orange-600" />
            </div>
            <h3 className="font-bold text-lg text-gray-900 mb-2">Real Question Discovery</h3>
            <p className="text-gray-600">Get 150+ questions parents & adults actually ask (with search volume)</p>
          </div>
          <div className="bg-white rounded-2xl p-8 border border-gray-200 shadow-sm hover:shadow-md hover:border-orange-200 transition-all duration-300 group">
            <div className="w-14 h-14 bg-orange-100 rounded-xl flex items-center justify-center mb-4 group-hover:bg-orange-200 transition-colors">
              <FileText className="w-7 h-7 text-orange-600" />
            </div>
            <h3 className="font-bold text-lg text-gray-900 mb-2">Answer Post Generator</h3>
            <p className="text-gray-600">Generate 1,600-2,300 word publish-ready answer posts</p>
          </div>
          <div className="bg-white rounded-2xl p-8 border border-gray-200 shadow-sm hover:shadow-md hover:border-orange-200 transition-all duration-300 group">
            <div className="w-14 h-14 bg-orange-100 rounded-xl flex items-center justify-center mb-4 group-hover:bg-orange-200 transition-colors">
              <Globe className="w-7 h-7 text-orange-600" />
            </div>
            <h3 className="font-bold text-lg text-gray-900 mb-2">Built-in SEO Structure</h3>
            <p className="text-gray-600">Titles, headings, meta description, FAQ block (done for you)</p>
          </div>
          <div className="bg-white rounded-2xl p-8 border border-gray-200 shadow-sm hover:shadow-md hover:border-orange-200 transition-all duration-300 group">
            <div className="w-14 h-14 bg-orange-100 rounded-xl flex items-center justify-center mb-4 group-hover:bg-orange-200 transition-colors">
              <LinkIcon className="w-7 h-7 text-orange-600" />
            </div>
            <h3 className="font-bold text-lg text-gray-900 mb-2">School CTAs + Links</h3>
            <p className="text-gray-600">Auto-insert CTAs for your programs + links to your pages</p>
          </div>
        </div>
      </section>

      {/* THE PROBLEM */}
      <section className="py-16 px-6">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl font-bold mb-6 text-gray-900">
            Most Martial Arts Schools Don't Have a Content Problem - <br className="hidden sm:block" />
            They Have a "Starting" Problem.
          </h2>
          <p className="text-lg text-gray-600 mb-8">
            You know you should be publishing. But without a clear list of what parents are actually asking, content stalls.
            So nothing gets posted... and if nothing gets posted, Google and AI have nothing to recommend.
          </p>
          <div className="flex flex-col sm:flex-row justify-center gap-4 text-left max-w-2xl mx-auto">
            <div className="flex items-start gap-3 bg-red-50 p-4 rounded-lg">
              <span className="text-red-500 text-xl">✗</span>
              <span className="text-gray-700">You don't know which topics will actually bring leads</span>
            </div>
            <div className="flex items-start gap-3 bg-red-50 p-4 rounded-lg">
              <span className="text-red-500 text-xl">✗</span>
              <span className="text-gray-700">You don't have time to write long, structured articles</span>
            </div>
            <div className="flex items-start gap-3 bg-red-50 p-4 rounded-lg">
              <span className="text-red-500 text-xl">✗</span>
              <span className="text-gray-700">Generic "AI writers" don't know your programs or location</span>
            </div>
          </div>
        </div>
      </section>

      {/* VISUAL BREAK - Instructor Image */}
      <section className="py-12 px-6 bg-gradient-to-r from-orange-600 to-orange-500">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
            <div className="text-white">
              <h2 className="text-3xl font-bold mb-4">Your Students Deserve to Be Found</h2>
              <p className="text-orange-100 text-lg mb-6">
                Every day, parents in your area are asking Google and AI assistants questions about martial arts. 
                When you have the answers on your website, you become the expert they trust.
              </p>
              <div className="flex flex-wrap gap-4">
                <div className="bg-white/20 backdrop-blur rounded-lg px-4 py-2">
                  <span className="font-bold">2,400+</span> monthly searches for "kids martial arts near me"
                </div>
                <div className="bg-white/20 backdrop-blur rounded-lg px-4 py-2">
                  <span className="font-bold">73%</span> of parents research online first
                </div>
              </div>
            </div>
            <div className="relative">
              <img 
                src="https://images.unsplash.com/photo-1643773396217-0a96b1cfe89c?w=600&q=80" 
                alt="Martial arts instructor with student" 
                className="rounded-2xl shadow-2xl w-full h-[350px] object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* THE SOLUTION - 4 STEP WORKFLOW */}
      <section id="how-it-works" className="py-16 px-6 bg-gray-50">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold mb-4 text-gray-900">From Questions to Published Posts in Minutes</h2>
            <p className="text-lg text-gray-600">Your static website isn't going to rank for these questions. Answer them in 4 simple steps.</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Step 1 */}
            <div className="bg-white rounded-2xl p-8 border border-gray-200">
              <div className="flex items-center gap-3 mb-4">
                <span className="w-10 h-10 bg-orange-600 rounded-full flex items-center justify-center text-white font-bold">1</span>
                <h3 className="text-xl font-bold text-gray-900">Build Your Profile</h3>
              </div>
              <p className="text-gray-600">Tell QuillFire about your business - services, location, audience, voice - so every post is personalized.</p>
            </div>

            {/* Step 2 */}
            <div className="bg-orange-50 rounded-2xl p-8 border-2 border-orange-200">
              <div className="flex items-center gap-3 mb-4">
                <span className="w-10 h-10 bg-orange-600 rounded-full flex items-center justify-center text-white font-bold">2</span>
                <h3 className="text-xl font-bold text-gray-900">Question Discovery</h3>
              </div>
              <p className="text-gray-600">Get 150+ real questions people ask in your market (with search volume), across local, pricing, and comparison intent.</p>
            </div>

            {/* Step 3 */}
            <div className="bg-orange-50 rounded-2xl p-8 border-2 border-orange-200">
              <div className="flex items-center gap-3 mb-4">
                <span className="w-10 h-10 bg-orange-600 rounded-full flex items-center justify-center text-white font-bold">3</span>
                <h3 className="text-xl font-bold text-gray-900">One-Click Answers</h3>
              </div>
              <p className="text-gray-600">Click a question. Generate a complete 1,600-2,300 word answer post with your business details, CTA, and internal links.</p>
            </div>

            {/* Step 4 */}
            <div className="bg-white rounded-2xl p-8 border border-gray-200">
              <div className="flex items-center gap-3 mb-4">
                <span className="w-10 h-10 bg-orange-600 rounded-full flex items-center justify-center text-white font-bold">4</span>
                <h3 className="text-xl font-bold text-gray-900">Publish Anywhere</h3>
              </div>
              <p className="text-gray-600">Export in one click - or publish directly to your website and platforms on Pro.</p>
            </div>
          </div>
        </div>
      </section>

      {/* REAL QUESTIONS SECTION */}
      <section id="questions" className="py-16 px-6">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold mb-4 text-gray-900">Real Questions Parents Ask Before They Enroll</h2>
            <p className="text-lg text-gray-600">These aren't random blog ideas. These are high-intent questions families ask right before choosing a school.</p>
          </div>

          {/* Question Categories Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-12">
            {questionCategories.map((category, idx) => (
              <div key={idx} className={`bg-${category.color}-50 rounded-2xl p-6 border border-${category.color}-200`}>
                <div className="flex items-center gap-3 mb-4">
                  <div className={`w-12 h-12 bg-${category.color}-100 rounded-xl flex items-center justify-center text-${category.color}-600`}>
                    {category.icon}
                  </div>
                  <h3 className="text-xl font-bold text-gray-900">{category.title}</h3>
                </div>
                <ul className="space-y-2">
                  {category.questions.map((q, qIdx) => (
                    <li key={qIdx} className="flex items-start gap-2 text-gray-700">
                      <span className="text-gray-400 mt-1">•</span>
                      <span>{q}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>

          {/* CTA under questions */}
          <div className="text-center">
            <Button 
              onClick={handleGetStarted}
              className="bg-orange-600 hover:bg-orange-700 text-white text-lg px-8 py-6 rounded-xl font-semibold"
            >
              Start Free Trial (5 Posts) →
            </Button>
            <p className="text-sm text-gray-500 mt-3">See your school's questions in minutes.</p>
          </div>
        </div>
      </section>

      {/* WHAT MAKES QUILLFIRE DIFFERENT - 2x2 Grid with Visible Images */}
      <section className="py-16 px-6 bg-gray-50">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold mb-4 text-gray-900">What Makes QuillFire Different for Martial Arts Schools</h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Anyone can generate a blog. QuillFire generates publish-ready posts built to convert—using your services, audiences, CTAs, and internal links.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Card 1 - CTA Library */}
            <div className="bg-white rounded-xl overflow-hidden border border-gray-200 shadow-sm hover:shadow-lg transition-shadow">
              <div className="bg-gradient-to-br from-orange-50 to-orange-100 p-4">
                <img 
                  src="/screenshot-cta-library.png" 
                  alt="QuillFire CTA Library" 
                  className="w-full h-[170px] object-cover object-top rounded-lg border border-gray-200 shadow-sm"
                />
              </div>
              <div className="p-5">
                <h3 className="text-lg font-bold text-gray-900 mb-2">CTAs That Match the Reader</h3>
                <p className="text-sm text-gray-600 mb-3">
                  Save multiple CTAs for different audiences—toddlers, families, adults—and insert the right one automatically.
                </p>
                <div className="flex flex-wrap gap-2">
                  <span className="text-xs bg-green-50 text-green-700 px-2 py-1 rounded-full">Higher conversions</span>
                  <span className="text-xs bg-green-50 text-green-700 px-2 py-1 rounded-full">Custom to your school</span>
                </div>
              </div>
            </div>

            {/* Card 2 - Links Library */}
            <div className="bg-white rounded-xl overflow-hidden border border-gray-200 shadow-sm hover:shadow-lg transition-shadow">
              <div className="bg-gradient-to-br from-blue-50 to-blue-100 p-4">
                <img 
                  src="/screenshot-links-library.png" 
                  alt="QuillFire Links Library" 
                  className="w-full h-[170px] object-cover object-top rounded-lg border border-gray-200 shadow-sm"
                />
              </div>
              <div className="p-5">
                <h3 className="text-lg font-bold text-gray-900 mb-2">Automatic Internal Links</h3>
                <p className="text-sm text-gray-600 mb-3">
                  Store your pages—programs, schedule, contact—then auto-insert links to build SEO authority.
                </p>
                <div className="flex flex-wrap gap-2">
                  <span className="text-xs bg-blue-50 text-blue-700 px-2 py-1 rounded-full">Better SEO structure</span>
                  <span className="text-xs bg-blue-50 text-blue-700 px-2 py-1 rounded-full">More conversions</span>
                </div>
              </div>
            </div>

            {/* Card 3 - Content Strategy Setup */}
            <div className="bg-white rounded-xl overflow-hidden border border-gray-200 shadow-sm hover:shadow-lg transition-shadow">
              <div className="bg-gradient-to-br from-purple-50 to-purple-100 p-4">
                <img 
                  src="/screenshot-profile-setup.png" 
                  alt="QuillFire Content Strategy Setup" 
                  className="w-full h-[170px] object-cover object-top rounded-lg border border-gray-200 shadow-sm"
                />
              </div>
              <div className="p-5">
                <h3 className="text-lg font-bold text-gray-900 mb-2">Your Strategy Built-In</h3>
                <p className="text-sm text-gray-600 mb-3">
                  Define programs, service areas, and audiences—then get topics that combine them naturally.
                </p>
                <div className="flex flex-wrap gap-2">
                  <span className="text-xs bg-purple-50 text-purple-700 px-2 py-1 rounded-full">Local relevance</span>
                  <span className="text-xs bg-purple-50 text-purple-700 px-2 py-1 rounded-full">Program-specific</span>
                </div>
              </div>
            </div>

            {/* Card 4 - Brand Voice */}
            <div className="bg-white rounded-xl overflow-hidden border border-gray-200 shadow-sm hover:shadow-lg transition-shadow">
              <div className="bg-gradient-to-br from-pink-50 to-pink-100 p-4 h-[202px] flex items-center justify-center">
                <div className="text-center">
                  <MessageSquare className="w-16 h-16 mx-auto mb-3 text-pink-500" />
                  <p className="text-lg font-semibold text-pink-700">Brand Voice & Tone</p>
                  <p className="text-pink-600 text-sm">Consistent messaging</p>
                </div>
              </div>
              <div className="p-5">
                <h3 className="text-lg font-bold text-gray-900 mb-2">Posts That Sound Like You</h3>
                <p className="text-sm text-gray-600 mb-3">
                  Set your tone—friendly, authoritative, or conversational—and every post matches your voice.
                </p>
                <div className="flex flex-wrap gap-2">
                  <span className="text-xs bg-pink-50 text-pink-700 px-2 py-1 rounded-full">Consistent voice</span>
                  <span className="text-xs bg-pink-50 text-pink-700 px-2 py-1 rounded-full">Your differentiators</span>
                </div>
              </div>
            </div>
          </div>

          {/* Why This Improves Quality Section - Compact */}
          <div className="mt-12 bg-white rounded-xl p-6 border border-gray-200">
            <h3 className="text-xl font-bold text-gray-900 mb-4 text-center">Why These Details Improve Quality (and Rankings)</h3>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              <div className="text-center p-3">
                <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center mx-auto mb-2">
                  <FileText className="w-5 h-5 text-orange-600" />
                </div>
                <p className="font-semibold text-sm text-gray-900">Long-form</p>
                <p className="text-xs text-gray-500">1,600–2,300 words</p>
              </div>
              <div className="text-center p-3">
                <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center mx-auto mb-2">
                  <MapPin className="w-5 h-5 text-orange-600" />
                </div>
                <p className="font-semibold text-sm text-gray-900">Localized</p>
                <p className="text-xs text-gray-500">Your cities built in</p>
              </div>
              <div className="text-center p-3">
                <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center mx-auto mb-2">
                  <Users className="w-5 h-5 text-orange-600" />
                </div>
                <p className="font-semibold text-sm text-gray-900">Targeted</p>
                <p className="text-xs text-gray-500">Right CTA for audience</p>
              </div>
              <div className="text-center p-3">
                <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center mx-auto mb-2">
                  <Zap className="w-5 h-5 text-orange-600" />
                </div>
                <p className="font-semibold text-sm text-gray-900">Actionable</p>
                <p className="text-xs text-gray-500">Links to your pages</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* WHY OUR BLOGS ARE DIFFERENT */}
      <section className="py-16 px-6">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold mb-4 text-gray-900">Not Just "AI Text." A Complete SEO Answer Post.</h2>
            <p className="text-lg text-gray-600">QuillFire generates long-form posts designed to match real questions and convert readers into leads.</p>
          </div>

          <div className="bg-gradient-to-r from-orange-50 to-orange-100/50 rounded-2xl p-8 border border-orange-200">
            <h3 className="font-bold text-lg text-gray-900 mb-4">Each post includes:</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {[
                "SEO Title + Meta Description",
                "1,600-2,300 words (complete answers, not short filler)",
                "Clean H1/H2/H3 structure (easy for Google + AI to interpret)",
                "FAQ block (optimized for conversational/voice-style queries)",
                "Custom CTA matched to the audience (parents vs adults)",
                "Internal link suggestions from your own website pages",
                "Optional featured image generated to match the topic",
                "Export: HTML / Doc / Copy-paste (and Pro auto-publish)"
              ].map((item, idx) => (
                <div key={idx} className="flex items-start gap-2">
                  <Check className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-700">{item}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* SAMPLE BLOG PREVIEW */}
      <section className="py-16 px-6 bg-gray-50">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold mb-4 text-gray-900">Example Post Preview (Generated in One Click)</h2>
          </div>

          <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden shadow-lg">
            {/* Mock browser bar */}
            <div className="bg-gray-100 px-4 py-2 border-b border-gray-200 flex items-center gap-2">
              <div className="flex gap-1.5">
                <div className="w-3 h-3 bg-red-400 rounded-full"></div>
                <div className="w-3 h-3 bg-yellow-400 rounded-full"></div>
                <div className="w-3 h-3 bg-green-400 rounded-full"></div>
              </div>
              <div className="flex-1 bg-white rounded px-3 py-1 text-sm text-gray-500 ml-4">
                yourschool.com/blog/what-age-start-martial-arts
              </div>
            </div>

            <div className="p-8">
              {/* Title */}
              <h1 className="text-2xl font-bold text-gray-900 mb-3">
                What Age Should a Child Start Martial Arts? (A Parent's Guide)
              </h1>
              
              {/* Meta description */}
              <p className="text-gray-500 text-sm italic mb-6 pb-4 border-b border-gray-200">
                Learn the best age to start martial arts, what to look for by age group, and how to pick the right program for your child.
              </p>

              {/* Outline preview */}
              <div className="mb-6">
                <p className="text-sm font-semibold text-gray-700 mb-3">Article Outline:</p>
                <ul className="space-y-2">
                  {sampleBlogOutline.map((item, idx) => (
                    <li key={idx} className="flex items-start gap-2 text-gray-600 text-sm">
                      <span className="text-orange-500 font-bold">H2</span>
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>

              {/* FAQ preview */}
              <div className="bg-blue-50 rounded-xl p-4 mb-6">
                <p className="text-sm font-semibold text-gray-700 mb-3">FAQ Section Preview:</p>
                <ul className="space-y-2">
                  {sampleFaqs.map((faq, idx) => (
                    <li key={idx} className="flex items-start gap-2 text-gray-600 text-sm">
                      <span className="text-blue-500">Q:</span>
                      <span>{faq}</span>
                    </li>
                  ))}
                </ul>
              </div>

              {/* CTA preview */}
              <div className="bg-orange-50 rounded-xl p-4 border border-orange-200">
                <p className="text-sm font-semibold text-gray-700 mb-2">Custom CTA:</p>
                <p className="text-gray-600 text-sm italic">
                  "Ready to see if martial arts is a fit for your child? Visit [School Name] in [City] and try a beginner-friendly class designed to build focus, confidence, and discipline."
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 7-DAY PUBLISHING PLAN */}
      <section className="py-16 px-6">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold mb-4 text-gray-900">Your 7-Day "Get Found" Publishing Plan</h2>
            <p className="text-lg text-gray-600">Martial Arts Edition</p>
          </div>

          <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden">
            {publishingPlan.map((topic, idx) => (
              <div key={idx} className={`flex items-center gap-4 p-4 ${idx !== publishingPlan.length - 1 ? 'border-b border-gray-100' : ''}`}>
                <div className="w-12 h-12 bg-orange-100 rounded-xl flex items-center justify-center flex-shrink-0">
                  <span className="font-bold text-orange-600">Day {idx + 1}</span>
                </div>
                <span className="text-gray-700">{topic}</span>
              </div>
            ))}
          </div>

          <p className="text-center text-gray-600 mt-6 italic">
            This isn't "content marketing." It's answering the exact questions families ask before they buy.
          </p>
        </div>
      </section>

      {/* INTEGRATIONS */}
      <section className="py-16 px-6 bg-gray-50">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl font-bold mb-4 text-gray-900">Publish Faster with One-Click Integrations</h2>
          <p className="text-lg text-gray-600 mb-8">(Pro Feature)</p>

          <div className="flex flex-wrap justify-center gap-4 mb-8">
            {["Export HTML/Doc anytime", "Pro: publish directly to your website", "Pro: post to Medium, LinkedIn, and GBP", "Internal links + CTAs inserted automatically"].map((item, idx) => (
              <div key={idx} className="flex items-center gap-2 bg-white px-4 py-2 rounded-full border border-gray-200">
                <Check className="w-4 h-4 text-green-500" />
                <span className="text-sm text-gray-700">{item}</span>
              </div>
            ))}
          </div>

          <Button 
            onClick={() => window.location.href = '/#pricing'}
            variant="outline"
            className="border-orange-600 text-orange-600 hover:bg-orange-50"
          >
            View Pro Plan →
          </Button>
        </div>
      </section>

      {/* PRICING */}
      <section id="pricing" className="py-16 px-6">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold mb-4 text-gray-900">Simple, Transparent Pricing</h2>
            <p className="text-lg text-gray-600">Best for martial arts schools that want consistent visibility without hiring a writer.</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {/* Free */}
            <div className="bg-white rounded-2xl p-8 border border-gray-200">
              <h3 className="text-xl font-bold text-gray-900 mb-2">Free Trial</h3>
              <p className="text-4xl font-bold text-gray-900 mb-1">$0</p>
              <p className="text-sm text-gray-500 mb-6">Lifetime access</p>
              <ul className="space-y-3 mb-8">
                <li className="flex items-start gap-2 text-gray-700 text-sm">
                  <Check className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                  <span><strong>5 answer posts</strong> (free trial)</span>
                </li>
                <li className="flex items-start gap-2 text-gray-700 text-sm">
                  <Check className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                  <span><strong>5 AI images</strong> (free trial)</span>
                </li>
                <li className="flex items-start gap-2 text-gray-700 text-sm">
                  <Check className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                  <span><strong>3 question discoveries</strong></span>
                </li>
                <li className="flex items-start gap-2 text-gray-700 text-sm">
                  <Check className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                  <span>Full SEO optimization</span>
                </li>
                <li className="flex items-start gap-2 text-gray-700 text-sm">
                  <Check className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                  <span>Export & download</span>
                </li>
                <li className="flex items-start gap-2 text-gray-400 text-sm">
                  <span className="w-4 h-4 mt-0.5 flex-shrink-0 text-center">✗</span>
                  <span>Auto-posting</span>
                </li>
                <li className="flex items-start gap-2 text-gray-400 text-sm">
                  <span className="w-4 h-4 mt-0.5 flex-shrink-0 text-center">✗</span>
                  <span>Content platforms</span>
                </li>
              </ul>
              <Button onClick={handleGetStarted} className="w-full bg-gray-900 hover:bg-gray-800 text-white">
                Start Free Trial
              </Button>
            </div>

            {/* Starter */}
            <div className="bg-white rounded-2xl p-8 border border-gray-200">
              <h3 className="text-xl font-bold text-gray-900 mb-2">Starter</h3>
              <p className="text-4xl font-bold text-gray-900 mb-1">$27<span className="text-lg font-normal text-gray-500">/mo</span></p>
              <p className="text-sm text-gray-500 mb-6">For growing schools</p>
              <ul className="space-y-3 mb-8">
                <li className="flex items-start gap-2 text-gray-700 text-sm">
                  <Check className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                  <span><strong>30 answer posts/month</strong> (1/day)</span>
                </li>
                <li className="flex items-start gap-2 text-gray-700 text-sm">
                  <Check className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                  <span><strong>30 AI images/month</strong></span>
                </li>
                <li className="flex items-start gap-2 text-gray-700 text-sm">
                  <Check className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                  <span><strong>5 question discoveries/mo</strong></span>
                </li>
                <li className="flex items-start gap-2 text-gray-700 text-sm">
                  <Check className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                  <span>Full SEO optimization</span>
                </li>
                <li className="flex items-start gap-2 text-gray-700 text-sm">
                  <Check className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                  <span>Content library</span>
                </li>
                <li className="flex items-start gap-2 text-gray-400 text-sm">
                  <span className="w-4 h-4 mt-0.5 flex-shrink-0 text-center">✗</span>
                  <span>Auto-posting</span>
                </li>
                <li className="flex items-start gap-2 text-gray-400 text-sm">
                  <span className="w-4 h-4 mt-0.5 flex-shrink-0 text-center">✗</span>
                  <span>Content platforms</span>
                </li>
              </ul>
              <Button onClick={handleGetStarted} className="w-full bg-blue-600 hover:bg-blue-700 text-white">
                Start with Starter
              </Button>
            </div>

            {/* Pro */}
            <div className="bg-gradient-to-br from-orange-50 to-orange-100 rounded-2xl p-8 border-2 border-orange-500 relative">
              <div className="absolute -top-3 left-1/2 transform -translate-x-1/2 bg-orange-500 text-white text-xs px-3 py-1 rounded-full font-semibold">
                MOST POPULAR
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">Pro</h3>
              <p className="text-4xl font-bold text-gray-900 mb-1">$67<span className="text-lg font-normal text-gray-500">/mo</span></p>
              <p className="text-sm text-gray-500 mb-6">For serious growth</p>
              <ul className="space-y-3 mb-8">
                <li className="flex items-start gap-2 text-gray-700 text-sm">
                  <Check className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                  <span><strong>90 answer posts/month</strong> (3/day)</span>
                </li>
                <li className="flex items-start gap-2 text-gray-700 text-sm">
                  <Check className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                  <span><strong>90 AI images/month</strong></span>
                </li>
                <li className="flex items-start gap-2 text-gray-700 text-sm">
                  <Check className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                  <span><strong>20 question discoveries/mo</strong></span>
                </li>
                <li className="flex items-start gap-2 text-gray-700 text-sm">
                  <Check className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                  <span>Full SEO optimization</span>
                </li>
                <li className="flex items-start gap-2 text-gray-700 text-sm">
                  <Check className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                  <span>Content library</span>
                </li>
                <li className="flex items-start gap-2 text-gray-700 text-sm">
                  <Check className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                  <span><strong>Auto-post to your website</strong></span>
                </li>
                <li className="flex items-start gap-2 text-gray-700 text-sm">
                  <Check className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                  <span><strong>Post to Medium, LinkedIn & GBP</strong></span>
                </li>
              </ul>
              <Button onClick={handleGetStarted} className="w-full bg-orange-600 hover:bg-orange-700 text-white">
                Go Pro
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ SECTION */}
      <section className="py-16 px-6 bg-gray-50">
        <div className="max-w-3xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold mb-4 text-gray-900">Martial Arts FAQ</h2>
          </div>

          <div className="space-y-4">
            {faqs.map((faq, idx) => (
              <div key={idx} className="bg-white rounded-xl border border-gray-200 overflow-hidden">
                <button
                  onClick={() => setExpandedFaq(expandedFaq === idx ? null : idx)}
                  className="w-full flex items-center justify-between p-4 text-left"
                >
                  <span className="font-medium text-gray-900">{faq.q}</span>
                  {expandedFaq === idx ? (
                    <ChevronUp className="w-5 h-5 text-gray-400" />
                  ) : (
                    <ChevronDown className="w-5 h-5 text-gray-400" />
                  )}
                </button>
                {expandedFaq === idx && (
                  <div className="px-4 pb-4 text-gray-600">
                    {faq.a}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="py-20 px-6 bg-gradient-to-r from-orange-600 to-red-600">
        <div className="max-w-4xl mx-auto text-center text-white">
          <h2 className="text-3xl sm:text-4xl font-bold mb-6">
            Start With 5 Free Posts - See Your School's Questions Instantly
          </h2>
          <Button 
            onClick={handleGetStarted}
            className="bg-white text-orange-600 hover:bg-orange-50 text-lg px-8 py-6 rounded-xl font-semibold"
          >
            Start Free Trial (5 Blogs) →
          </Button>
          <p className="text-sm text-orange-100 mt-4">
            No credit card required • 5 answer posts + 5 images included
          </p>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-12 px-6">
        <div className="max-w-6xl mx-auto">
          <div className="flex flex-col md:flex-row justify-between items-center gap-6">
            <div className="flex items-center space-x-2">
              <img src="/quillfire-logo.png" alt="QuillFire" className="w-8 h-8 rounded" />
              <span className="text-xl font-bold">QuillFire</span>
            </div>
            <div className="flex gap-6 text-sm text-gray-400">
              <a href="/" className="hover:text-white">Home</a>
              <a href="/#pricing" className="hover:text-white">Pricing</a>
              <a href="/#features" className="hover:text-white">Features</a>
            </div>
            <p className="text-sm text-gray-500">© 2024 QuillFire. All rights reserved.</p>
          </div>
        </div>
      </footer>

      {/* Auth Modal */}
      {showAuth && <AuthModal onClose={() => setShowAuth(false)} onLogin={handleLogin} />}
    </div>
  );
};

export default IndustryMartialArts;
